// ── Plan card helpers (issue #780) ─────────────────────────────────────────
// Pure functions used by apps/web/src/components/PlanCard.tsx. Extracted
// here so they can be unit-tested without rendering React.
//
// The plan card surfaces three things to every user:
//   1. Plan name (humanised) — sourced from @blueprint/shared
//   2. Pricing display — region-aware formatPrice for STARTER/GROWTH;
//      tier-specific copy for non-priceable tiers
//   3. Upgrade CTA — only for self-serve tiers below the ceiling
//
// The shared tier-display module owns (1) and (3) since they're shared
// with the API billing service. This module owns (2) since pricing
// strings are a frontend concern (and depend on currency.ts).

import { detectCurrency, formatTierPrice, isPriceableTier, type Currency } from './currency';

export interface PricingDisplay {
  /** Main label, e.g. "$79/seat/month" or "Custom — contact your account manager" */
  label: string;
  /** When false, render the label as a muted note rather than a numeric price */
  numeric: boolean;
}

/**
 * Render-ready pricing copy for a tier.
 *
 * - STARTER / GROWTH: numeric price via formatTierPrice (region-aware)
 * - INDIVIDUAL: "Solo plan — free during beta"
 * - ENTERPRISE: contract copy
 * - PLATFORM_PARTNER: partner-agreement copy
 * - everything else: empty label so the row doesn't render
 */
export function getPricingDisplay(
  tier: string | null | undefined,
  opts: { locale?: string | null; timezone?: string | null; browserLocale?: string | null } = {},
): PricingDisplay {
  if (isPriceableTier(tier)) {
    const currency: Currency = detectCurrency({
      locale:        opts.locale ?? null,
      timezone:      opts.timezone ?? null,
      browserLocale: opts.browserLocale ?? null,
    });
    return { label: formatTierPrice(tier, currency), numeric: true };
  }
  if (tier === 'INDIVIDUAL') {
    return { label: 'Solo plan — free during beta', numeric: false };
  }
  if (tier === 'ENTERPRISE') {
    return { label: 'Custom — contact your account manager', numeric: false };
  }
  if (tier === 'PLATFORM_PARTNER') {
    return { label: 'Billed via partner agreement', numeric: false };
  }
  return { label: '', numeric: false };
}
